import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  format,
  addMonths,
  subMonths,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from "date-fns";
import el from "date-fns/locale/el";
import {
  CalendarIcon,
  CheckCircle2,
  Check,
  Clock,
  Send,
  ChevronLeft,
  ChevronRight,
  Phone,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  appointmentApi,
  chatbotApi,
  TimeSlot,
  AppointmentData,
} from "../services/api";

// Latin-to-Greek homoglyph map for visually identical characters
const LATIN_TO_GREEK: Record<string, string> = {
  'A': 'Α', 'B': 'Β', 'E': 'Ε', 'Z': 'Ζ', 'H': 'Η', 'I': 'Ι',
  'K': 'Κ', 'M': 'Μ', 'N': 'Ν', 'O': 'Ο', 'P': 'Ρ', 'T': 'Τ',
  'Y': 'Υ', 'X': 'Χ',
  'a': 'α', 'e': 'ε', 'i': 'ι', 'o': 'ο', 'n': 'ν', 'v': 'ν',
};

/**
 * Normalize a name for Greek validation:
 * 1. Unicode NFC normalization (handles decomposed accents from macOS keyboards)
 * 2. Convert Latin homoglyphs to their Greek equivalents
 */
function normalizeGreekName(value: string): string {
  // Step 1: Unicode NFC normalization
  let normalized = value.normalize('NFC');
  // Step 2: Replace Latin homoglyphs with Greek equivalents
  normalized = normalized
    .split('')
    .map((ch) => LATIN_TO_GREEK[ch] ?? ch)
    .join('');
  return normalized;
}

// Updated regex: includes dialytika characters (ϊ ϋ Ϊ Ϋ ΐ ΰ)
const GREEK_NAME_REGEX = /^[Α-Ωα-ωΆΈΉΊΌΎΏάέήίόύώϊϋΪΫΐΰ\s\-']+$/;

const appointmentSchema = z.object({
  patient_surname: z
    .string()
    .min(2, "Το επώνυμο πρέπει να έχει τουλάχιστον 2 χαρακτήρες")
    .transform(normalizeGreekName)
    .refine(
      (val) => GREEK_NAME_REGEX.test(val),
      "Το επώνυμο πρέπει να περιέχει μόνο ελληνικούς χαρακτήρες",
    ),
  patient_name: z
    .string()
    .min(2, "Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες")
    .transform(normalizeGreekName)
    .refine(
      (val) => GREEK_NAME_REGEX.test(val),
      "Το όνομα πρέπει να περιέχει μόνο ελληνικούς χαρακτήρες",
    ),
  phone: z.string().regex(/^\d{10}$/, "Το κινητό πρέπει να έχει 10 ψηφία"),
  amka: z
    .string()
    .length(11, "Ο ΑΜΚΑ πρέπει να έχει ακριβώς 11 ψηφία")
    .regex(/^\d{11}$/, "Ο ΑΜΚΑ πρέπει να περιέχει μόνο αριθμούς"),
  email: z.string().email("Παρακαλώ εισάγετε ένα έγκυρο email"),
  notes: z.string().optional(),
  is_returning_patient: z.boolean().default(false),
});

type AppointmentFormData = z.infer<typeof appointmentSchema> & {
  appointment_datetime?: string;
};

// Cool accent (buttons / interactive) and selected-state colors
const ACCENT = "#c5d1e0"; // cool blue — booking buttons / calendar / slots
const SELECTED = "#d7886f"; // terracotta — selected day / time
const CHAT = "#ddd3c6"; // warm sand — phone button & chatbot

const WEEKDAY_LABELS = ["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"];

export const AppointmentBooking: React.FC = () => {
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [currentMonth, setCurrentMonth] = useState<Date>(() =>
    startOfMonth(addMonths(new Date(), 1)),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);
  const [chatInput, setChatInput] = useState("");
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [formError, setFormError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
  });

  useEffect(() => {
    loadAvailableSlots();
  }, []);

  // Keep a ref of the current selection so the 60s auto-refresh can avoid
  // reloading (and swapping out) the slot a patient is mid-booking.
  const selectedSlotRef = useRef<TimeSlot | null>(null);
  useEffect(() => {
    selectedSlotRef.current = selectedSlot;
  }, [selectedSlot]);

  useEffect(() => {
    const id = setInterval(() => {
      // Skip the background refresh while a slot is selected, so the patient's
      // chosen time can't disappear from under them mid-booking.
      if (!selectedSlotRef.current) {
        loadAvailableSlots();
      }
    }, 60000);
    return () => clearInterval(id);
  }, []);

  // Allow closing the chat assistant with Escape (no focus trap).
  useEffect(() => {
    if (!chatbotOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setChatbotOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chatbotOpen]);

  useEffect(() => {
    // Initialize selection to the earliest available date and show its month
    if (!selectedDate && availableSlots.length > 0) {
      const firstIso = availableSlots[0].slot_datetime;
      setSelectedDate(format(parseISO(firstIso), "yyyy-MM-dd"));
      setCurrentMonth(startOfMonth(parseISO(firstIso)));
    }
  }, [availableSlots, selectedDate]);

  const loadAvailableSlots = async () => {
    try {
      const startDate = addMonths(new Date(), 1);
      const endDate = addMonths(startDate, 2);
      const slots = await appointmentApi.getAvailableSlots(startDate, endDate);
      if (Array.isArray(slots)) {
        setAvailableSlots(slots);
      } else {
        console.warn("Expected array of slots but received:", slots);
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error("Error loading slots:", error);
    }
  };

  const onSubmit = async (data: AppointmentFormData) => {
    setFormError("");
    if (!selectedSlot) {
      setFormError("Παρακαλώ επιλέξτε μια διαθέσιμη ημέρα και ώρα στο Βήμα 1.");
      return;
    }

    setIsLoading(true);
    try {
      const appointmentData: AppointmentData = {
        ...data,
        appointment_datetime: selectedSlot.slot_datetime,
      };

      await appointmentApi.bookAppointment(appointmentData);
      setBookingSuccess(true);
      reset();
      setSelectedSlot(null);
      setFormError("");
      loadAvailableSlots();
    } catch (error: any) {
      console.error("Error booking appointment:", error);
      // Surface the backend's specific reason (e.g. an existing same-day
      // appointment for this ΑΜΚΑ) instead of a generic message.
      const detail = error?.response?.data?.detail;
      setFormError(
        typeof detail === "string" && detail
          ? detail
          : "Υπήρξε ένα σφάλμα κατά την κράτηση. Παρακαλώ δοκιμάστε ξανά.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput;
    setChatMessages((prev) => [
      ...prev,
      { role: "user", content: userMessage },
    ]);
    setChatInput("");

    try {
      const response = await chatbotApi.sendMessage(userMessage, sessionId);
      setSessionId(response.session_id);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.response },
      ]);
    } catch (error) {
      console.error("Error sending chat message:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Λυπάμαι, αντιμετωπίζω τεχνικό πρόβλημα. Παρακαλώ χρησιμοποιήστε τη φόρμα κράτησης.",
        },
      ]);
    }
  };

  const groupSlotsByDate = (slots: TimeSlot[] | unknown) => {
    const grouped: { [key: string]: TimeSlot[] } = {};
    if (!Array.isArray(slots)) return grouped;
    slots.forEach((slot) => {
      const date = format(parseISO(slot.slot_datetime), "yyyy-MM-dd");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    return grouped;
  };

  const groupedSlots = groupSlotsByDate(availableSlots);

  // Count of available slots per date (drives the calendar highlighting)
  const availableByDate: Record<string, number> = {};
  Object.entries(groupedSlots).forEach(([date, slots]) => {
    const count = slots.filter((s) => s.is_available).length;
    if (count > 0) availableByDate[date] = count;
  });
  const hasAnyAvailability = Object.keys(availableByDate).length > 0;

  const daySlots = selectedDate ? groupedSlots[selectedDate] || [] : [];

  // Month navigation is bounded to the bookable window (≈ now+1mo … now+3mo)
  const minMonth = startOfMonth(addMonths(new Date(), 1));
  const maxMonth = startOfMonth(addMonths(new Date(), 3));
  const prevDisabled = currentMonth.getTime() <= minMonth.getTime();
  const nextDisabled = currentMonth.getTime() >= maxMonth.getTime();

  const monthStart = startOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({
    start: monthStart,
    end: endOfMonth(currentMonth),
  });
  const leadingBlanks = (monthStart.getDay() + 6) % 7; // Monday-first grid

  const selectDay = (dateStr: string) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setFormError("");
  };

  if (bookingSuccess) {
    return (
      <Card className="max-w-2xl mx-auto mt-8">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-[#d0ddc6] flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-[#2b2d30]" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-[#3a4a62]">
              Επιτυχής Κράτηση!
            </h2>
            <p className="text-gray-600">
              Το ραντεβού σας καταχωρήθηκε επιτυχώς. Θα λάβετε email
              επιβεβαίωσης σύντομα.
            </p>
            <p className="text-sm text-gray-500 font-semibold mt-2">
              Στο email θα βρείτε και σύνδεσμο για ακύρωση του ραντεβού, εάν
              χρειαστεί.
            </p>
            <Button onClick={() => setBookingSuccess(false)}>
              Νέα Κράτηση
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div className="text-center sm:text-left">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          Κράτηση Ραντεβού
        </h1>
        <p className="text-muted-foreground mt-1">
          Επιλέξτε ημέρα και ώρα, και συμπληρώστε τα στοιχεία σας.
        </p>
      </div>

      {/* 30-day note, with a tap-to-call pill floating above its top-right */}
      <div className="relative">
        <a
          href="tel:+302310222799"
          aria-label="Κλήση Γραμματείας, 2310-222799"
          className="absolute right-3 -top-4 z-10 inline-flex items-center gap-2 rounded-full bg-[#ddd3c6] px-4 py-1.5 text-sm font-semibold text-[#2b2d30] shadow-md hover:bg-[#cfc2b0] transition-colors"
        >
          <Phone className="w-4 h-4" />
          2310-222799
        </a>
        <div className="bg-[#d0ddc6] rounded-lg p-4 pt-6">
          <p className="text-sm text-[#2b2d30] leading-relaxed">
            <strong>Σημείωση:</strong> Η πλατφόρμα online κράτησης ραντεβού αφορά
            μόνο μη επείγοντα ραντεβού που προγραμματίζονται σε διάστημα
            μεγαλύτερο των 30 ημερών από την τρέχουσα ημερομηνία. Για διαθέσιμα
            ραντεβού νωρίτερα ή επείγοντα περιστατικά, παρακαλούμε καλέστε τη
            Γραμματεία στο <strong>2310-222799</strong>.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* STEP 1 — choose day & time (the hero) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl tracking-tight text-[#3a4a62]">
              Βήμα 1 · Επιλέξτε ημέρα &amp; ώρα
            </CardTitle>
            <CardDescription>
              Οι ημέρες με διαθέσιμα ραντεβού (μετά από 30 ημέρες από σήμερα)
              είναι τονισμένες. Επιλέξτε μία για να δείτε τις διαθέσιμες ώρες
              ραντεβού (slots).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Calendar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() =>
                    !prevDisabled &&
                    setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)))
                  }
                  disabled={prevDisabled}
                  aria-label="Προηγούμενος μήνας"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-md hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-base font-semibold capitalize">
                  {format(currentMonth, "LLLL yyyy", { locale: el })}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    !nextDisabled &&
                    setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)))
                  }
                  disabled={nextDisabled}
                  aria-label="Επόμενος μήνας"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-md hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
                {WEEKDAY_LABELS.map((d) => (
                  <div key={d} className="py-1">
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: leadingBlanks }).map((_, i) => (
                  <div key={`blank-${i}`} />
                ))}
                {monthDays.map((day) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const count = availableByDate[dateStr] || 0;
                  const isAvail = count > 0;
                  const isSel = dateStr === selectedDate;
                  const cls = !isAvail
                    ? "text-foreground/25 cursor-default"
                    : isSel
                      ? "text-white font-semibold ring-2 ring-offset-1 ring-[#2b2d30]"
                      : "text-[#2b2d30] font-medium";
                  return (
                    <button
                      key={dateStr}
                      type="button"
                      disabled={!isAvail}
                      onClick={() => selectDay(dateStr)}
                      aria-pressed={isSel}
                      aria-label={`${format(day, "d MMMM yyyy", {
                        locale: el,
                      })}${
                        isAvail
                          ? `, ${count} διαθέσιμες ώρες ραντεβού`
                          : ", μη διαθέσιμη"
                      }`}
                      title={
                        isAvail ? `${count} slots` : undefined
                      }
                      style={
                        isAvail
                          ? {
                              backgroundColor: isSel ? SELECTED : ACCENT,
                            }
                          : undefined
                      }
                      className={`flex flex-col items-center justify-center min-h-[44px] rounded-md transition-colors ${cls}`}
                    >
                      <span className="text-sm leading-none">
                        {format(day, "d")}
                      </span>
                      {isAvail && (
                        <span
                          className={`mt-1 text-[10px] leading-none ${
                            isSel ? "text-white/90" : "text-[#2b2d30]/70"
                          }`}
                        >
                          {count} slots
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {!hasAnyAvailability && (
                <p className="text-sm text-muted-foreground">
                  Δεν υπάρχουν διαθέσιμα ραντεβού αυτή τη στιγμή. Παρακαλούμε
                  δοκιμάστε αργότερα ή καλέστε τη Γραμματεία στο 2310-222799.
                </p>
              )}
            </div>

            {/* Times for the selected day */}
            {selectedDate && (
              <div className="space-y-3 border-t pt-5">
                <h4 className="font-semibold text-foreground/80 flex items-center">
                  <CalendarIcon className="inline w-4 h-4 mr-2" />
                  {format(parseISO(selectedDate), "EEEE, d MMMM yyyy", {
                    locale: el,
                  })}
                </h4>
                {daySlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {daySlots.map((slot) => {
                      const isSel = selectedSlot?.id === slot.id;
                      return (
                        <Button
                          key={slot.id}
                          type="button"
                          size="sm"
                          onClick={() => {
                            setSelectedSlot(slot);
                            setFormError("");
                          }}
                          disabled={!slot.is_available}
                          aria-pressed={isSel}
                          aria-label={`Ώρα ${format(
                            parseISO(slot.slot_datetime),
                            "HH:mm",
                          )}${isSel ? " — επιλεγμένο" : ""}`}
                          style={{
                            backgroundColor: isSel ? SELECTED : ACCENT,
                            borderColor: isSel ? SELECTED : ACCENT,
                          }}
                          className={
                            isSel
                              ? "min-h-[44px] text-white font-semibold ring-2 ring-offset-1 ring-[#2b2d30] hover:opacity-90"
                              : "min-h-[44px] text-[#1f2937] hover:opacity-90"
                          }
                        >
                          {isSel ? (
                            <Check className="w-4 h-4 mr-1" />
                          ) : (
                            <Clock className="w-3 h-3 mr-1" />
                          )}
                          {format(parseISO(slot.slot_datetime), "HH:mm")}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-foreground/60">
                    Δεν υπάρχουν διαθέσιμα ραντεβού για την επιλεγμένη ημερομηνία.
                  </div>
                )}
              </div>
            )}

            {selectedSlot && (
              <p className="text-sm font-medium text-[#a64f3c] bg-[#f7ece8] border border-[#e6c9c0] rounded-md px-3 py-2">
                ✓ Επιλεγμένο ραντεβού:{" "}
                {format(
                  parseISO(selectedSlot.slot_datetime),
                  "EEEE, d MMMM yyyy, HH:mm",
                  { locale: el },
                )}
              </p>
            )}
          </CardContent>
        </Card>

        {/* STEP 2 — your details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl tracking-tight text-[#3a4a62]">Βήμα 2 · Τα στοιχεία σας</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="patient_surname">Επώνυμο *</Label>
                <Input
                  id="patient_surname"
                  {...register("patient_surname")}
                  placeholder="Τουλής"
                  aria-invalid={!!errors.patient_surname}
                  aria-describedby={
                    errors.patient_surname ? "err-surname" : undefined
                  }
                />
                {errors.patient_surname && (
                  <p id="err-surname" className="text-sm text-[#a64f3c]">
                    {errors.patient_surname.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="patient_name">Όνομα *</Label>
                <Input
                  id="patient_name"
                  {...register("patient_name")}
                  placeholder="Κωνσταντίνος"
                  aria-invalid={!!errors.patient_name}
                  aria-describedby={
                    errors.patient_name ? "err-name" : undefined
                  }
                />
                {errors.patient_name && (
                  <p id="err-name" className="text-sm text-[#a64f3c]">
                    {errors.patient_name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Κινητό *</Label>
                <Input
                  id="phone"
                  {...register("phone")}
                  placeholder="69XXXXXXXX"
                  inputMode="numeric"
                  maxLength={10}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "err-phone" : undefined}
                />
                {errors.phone && (
                  <p id="err-phone" className="text-sm text-[#a64f3c]">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="amka">ΑΜΚΑ *</Label>
                <Input
                  id="amka"
                  {...register("amka")}
                  placeholder="12345678901"
                  maxLength={11}
                  aria-invalid={!!errors.amka}
                  aria-describedby={errors.amka ? "err-amka" : undefined}
                />
                {errors.amka && (
                  <p id="err-amka" className="text-sm text-[#a64f3c]">
                    {errors.amka.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder="email@example.com"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "err-email" : undefined}
              />
              {errors.email && (
                <p id="err-email" className="text-sm text-[#a64f3c]">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Σημειώσεις (προαιρετικό)</Label>
              <Input
                id="notes"
                {...register("notes")}
                placeholder="Αναφέρετε τυχόν πληροφορίες που θα βοηθήσουν..."
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                id="is_returning_patient"
                type="checkbox"
                {...register("is_returning_patient")}
                className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                style={{ accentColor: "#3a4a62" }}
              />
              <Label
                htmlFor="is_returning_patient"
                className="font-normal cursor-pointer"
              >
                Έχω ξαναεπισκεφθεί το ιατρείο
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* STEP 3 — confirm */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl tracking-tight text-[#3a4a62]">Βήμα 3 · Επιβεβαίωση</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Για την τελική καταχώρηση, παρακαλούμε επιβεβαιώστε το ραντεβού
              σας (θα λάβετε επιβεβαίωση στο email που έχετε δηλώσει).
            </p>

            {formError && (
              <p
                role="alert"
                className="text-sm font-medium text-[#a64f3c] bg-[#f7ece8] border border-[#e6c9c0] rounded-md px-3 py-2"
              >
                {formError}
              </p>
            )}

            <Button
              type="submit"
              className="w-full min-h-[44px] bg-[#c5d1e0] text-[#1f2937] hover:bg-[#1f2937] hover:text-white focus:bg-[#1f2937] focus:text-white disabled:bg-[#f8f7f2] disabled:text-[#6b7280] disabled:border disabled:border-[#d1d5db] disabled:opacity-100 disabled:cursor-not-allowed"
              disabled={isLoading || !selectedSlot}
            >
              {isLoading ? "Καταχώρηση..." : "Κράτηση Ραντεβού"}
            </Button>
          </CardContent>
        </Card>
      </form>

      {/* Chatbot Launcher — docked bottom-right */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        {!chatbotOpen && (
          <div
            className="flex items-center gap-2 text-[#2b2d30] px-3 py-1 rounded-full shadow-lg"
            style={{ backgroundColor: CHAT }}
          >
            <img
              src="/chatbot-avatar.png"
              alt="Βοηθός"
              className="h-6 w-6 rounded-full"
            />
            <span className="text-sm font-semibold">Ρώτα με!</span>
          </div>
        )}
        <Button
          onClick={() => setChatbotOpen(!chatbotOpen)}
          className="rounded-full w-16 h-16 shadow-lg text-[#2b2d30] border-0 p-0 hover:opacity-90"
          style={{ backgroundColor: CHAT }}
          variant="ghost"
          aria-label={chatbotOpen ? "Κλείσιμο βοηθού" : "Άνοιγμα βοηθού"}
        >
          <img
            src="/chatbot-avatar.png"
            alt="Βοηθός Ραντεβού"
            className="w-10 h-10 rounded-full object-contain"
          />
        </Button>
      </div>

      {/* Chatbot Window */}
      {chatbotOpen && (
        <Card
          role="dialog"
          aria-label="Βοηθός Ραντεβού"
          className="fixed bottom-24 right-4 z-50 w-[calc(100vw-2rem)] max-w-sm h-[28rem] max-h-[70vh] shadow-xl flex flex-col"
        >
          <CardHeader className="pb-3 bg-[#f0eee6] text-[#2b2d30] border-b border-[#d0cdc6]">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                <CardTitle className="text-lg">Βοηθός Ραντεβού</CardTitle>
                <CardDescription className="text-xs text-[#2b2d30]/80 font-medium">
                  Ρωτήστε με για διαθεσιμότητα ή βοήθεια με την κράτηση
                </CardDescription>
              </div>
              <button
                type="button"
                onClick={() => setChatbotOpen(false)}
                aria-label="Κλείσιμο βοηθού"
                className="-mr-1 -mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-black/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 min-h-0 pt-4">
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {chatMessages.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-4">
                  Γεια σας! Πώς μπορώ να σας βοηθήσω με το ραντεβού σας;
                </div>
              )}
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  style={
                    msg.role === "user"
                      ? { backgroundColor: CHAT }
                      : undefined
                  }
                  className={`p-2 rounded-lg max-w-[80%] ${
                    msg.role === "user"
                      ? "text-[#2b2d30] ml-auto"
                      : "bg-[#f0eee6] text-[#2b2d30] border border-[#e2dfd6]"
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendChatMessage()}
                placeholder="Πληκτρολογήστε το μήνυμά σας..."
                aria-label="Μήνυμα προς τον βοηθό"
                className="flex-1 min-h-[44px]"
              />
              <Button
                onClick={sendChatMessage}
                size="icon"
                aria-label="Αποστολή μηνύματος"
                style={{ backgroundColor: CHAT }}
                className="min-h-[44px] min-w-[44px] text-[#2b2d30] hover:opacity-90"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
